"use client";
import { IUserData, IUsersBrandAccounts } from "@/types";
import { Label } from "@/components/ui/label";
import React from "react";
import { SetBio } from "@/lib/api";
import { toast } from "sonner";
import * as Impersonate from "@/lib/impersonate_api";
import { Textarea } from "@/components/ui/textarea";

interface Props {
    user: IUserData;
    updateUser: IUserData;
    setUpdateUser: React.Dispatch<React.SetStateAction<IUserData>>;
    admin?: boolean;
    className?: string;
    impersonate?: boolean;
    ws?: WebSocket | null;
    loggedInUser?: IUsersBrandAccounts | undefined;
}

function createDomainLink(bio: string): React.JSX.Element {
    const domainPattern =
        /([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?:\/([\w./-]+))?/g;

    const content: React.ReactNode[] = [];
    let lastIndex = 0;

    for (const match of bio.matchAll(domainPattern)) {
        const fullMatch = match[0];
        const domain = match[1];
        const path = match[2];
        const index = match.index ?? 0;

        if (index > lastIndex) {
            content.push(bio.slice(lastIndex, index));
        }

        const href = `https://${domain}${path ? `/${path}` : ""}`;

        content.push(
            <a
                key={`${index}-${fullMatch}`}
                className="underline"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {fullMatch}
            </a>,
        );

        lastIndex = index + fullMatch.length;
    }

    if (lastIndex < bio.length) {
        content.push(bio.slice(lastIndex));
    }

    return (
        <p
            id="user-bio"
            className="whitespace-pre-wrap text-lg"
        >
            {content}
        </p>
    );
}
export default function Biography({
    user,
    updateUser,
    setUpdateUser,
    admin,
    className,
    impersonate,
    ws,
    loggedInUser,
}: Props) {
    const [bio, setBio] = React.useState(updateUser.bio);
    const onBioBlur = async () => {
        if (user.bio === updateUser.bio) return;
        const toastid = toast("Updating bio...");
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_header",
                    data: {
                        pic: updateUser.pic,
                        displayname: updateUser.displayname,
                        bio: updateUser.bio,
                        pronouns: updateUser.settings.pronouns,
                        location: updateUser.settings.location,
                        layout: updateUser.theme.layout,
                        design: updateUser.design,
                    },
                })
            );
        }
        let response: any;
        if (impersonate) {
            response = await Impersonate.SetBio(user, updateUser.bio);
        } else {
            response = await SetBio(user, updateUser.bio);
        }
        response = await response.json();
        if (!response.error) {
            // Update the user object directly
            user.bio = updateUser.bio;
            toast.success("Updated", { id: toastid });
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(response),
                    id: toastid,
                }
            );
        }
    };

    return (
        <>
            {admin ? (
                <div className="flex flex-col gap-1">
                    <Textarea
                        label="Biography"
                        variant="card"
                        className="w-full resize-none"
                        maxLength={120}
                        value={bio}
                        onChange={(e) => {
                            setBio(e.target.value);
                            setUpdateUser({
                                ...updateUser,
                                bio: e.target.value,
                            });
                        }}
                        onBlur={onBioBlur}
                    />
                    <div className="flex items-center justify-between">
                        <Label variant="subtoggle" className="">
                            {updateUser.bio.length}/120
                        </Label>
                        <Label variant="subtoggle" className="ml-auto">
                            Max 120 characters
                        </Label>
                    </div>
                </div>
            ) : updateUser.bio === "" ? (
                <Label variant="toggle" className={`${!admin && "hidden"}`}>
                    No bio set
                </Label>
            ) : (
                createDomainLink(updateUser.bio)
            )}
        </>
    );
}
